import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Chip, Collapse, Link, Stack, TextField, Typography,
} from '@mui/material';
import { validateExpression } from '../../../services/violationService';

/**
 * Free-text formula editing, validated by the SERVER.
 *
 * There is deliberately no parser here. The API owns the one real parser, so what
 * this shows is exactly what will be stored — a client-side approximation would
 * be a second implementation of the language, which is the thing this design set
 * out to delete.
 */

FormulaRuleEditor.propTypes = {
  expression: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onValidation: PropTypes.func.isRequired,
  channels: PropTypes.array,
};

const EXAMPLES = [
  { expression: 'co2 > 1000', explains: 'a plain threshold' },
  { expression: 'co2 > 1000 for 5m', explains: 'only if it lasts 5 minutes' },
  { expression: 'avg(co2, 10m) > 800', explains: 'a 10-minute average' },
  { expression: 'max(mercaptan, 1m) > 0.5', explains: 'any spike within a minute' },
  { expression: 'slope(temperature, 5m) > 2', explains: 'rising faster than 2 per minute' },
  { expression: 'co2 > 1000 and humidity > 70', explains: 'two conditions together' },
  {
    expression: 'band co2 { Good: <800, Fair: <1000, Poor: <1500, Bad: * }',
    explains: 'graded bands instead of a yes/no breach',
  },
  { expression: 'band aqi(pm2_5, 24h) { Good: <51, Moderate: <101, Unhealthy: * }', explains: 'EPA AQI categories' },
];

const DEBOUNCE_MS = 400;

function FormulaRuleEditor({ expression = '', onChange, onValidation, channels = [] }) {
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    const text = (expression || '').trim();
    if (!text) {
      setResult(null);
      onValidation({ valid: false, empty: true });
      return undefined;
    }
    setChecking(true);
    const id = requestId.current + 1;
    requestId.current = id;

    const timer = setTimeout(() => {
      validateExpression(text)
        .then((r) => {
          // Ignore a response that arrived after a newer keystroke, or the editor
          // would flash an error belonging to text the user has already replaced.
          if (requestId.current !== id) return;
          setResult(r);
          setChecking(false);
          onValidation(r);
        })
        .catch(() => {
          if (requestId.current !== id) return;
          setChecking(false);
          const failure = {
            valid: false,
            error: { message: 'Could not reach the server to check this rule.', offset: null },
          };
          setResult(failure);
          onValidation(failure);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [expression, onValidation]);

  const error = result && !result.valid ? result.error : null;

  return (
    <Stack spacing={2}>
      <TextField
        label="Formula"
        value={expression}
        onChange={(e) => onChange(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        error={Boolean(error)}
        inputProps={{ spellCheck: false, autoCapitalize: 'off', autoCorrect: 'off' }}
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 14 } }}
      />

      {error && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          <Typography variant="body2">{error.message}</Typography>
          {Number.isInteger(error.offset) && (
            // Points at the exact character the parser stopped on, which is far
            // more use than restating the message.
            <Box component="pre" sx={{ m: 0, mt: 0.5, fontFamily: 'monospace', fontSize: 13, overflowX: 'auto' }}>
              {expression}
              {'\n'}
              {`${' '.repeat(Math.max(error.offset, 0))}^`}
            </Box>
          )}
        </Alert>
      )}

      {result?.valid && (
        <Alert severity="success" icon={false} sx={{ py: 0.5 }}>
          <Typography variant="body2">{result.description}</Typography>
        </Alert>
      )}

      {checking && !error && (
        <Typography variant="caption" color="text.secondary">
          Checking…
        </Typography>
      )}

      <Box>
        <Link component="button" type="button" variant="caption" onClick={() => setShowHelp((v) => !v)}>
          {showHelp ? 'Hide' : 'Show'} what you can write
        </Link>
        <Collapse in={showHelp}>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {EXAMPLES.map((ex) => (
              <Stack key={ex.expression} direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                <Box
                  component="code"
                  onClick={() => onChange(ex.expression)}
                  sx={{
                    fontFamily: 'monospace', fontSize: 13, px: 0.75, py: 0.25,
                    bgcolor: 'action.hover', borderRadius: 0.5, cursor: 'pointer',
                  }}
                >
                  {ex.expression}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {ex.explains}
                </Typography>
              </Stack>
            ))}
            {channels.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  Channel names you can use:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {channels.map((c) => (
                    <Chip
                      key={c.groupKey}
                      size="small"
                      variant="outlined"
                      label={c.groupKey}
                      title={c.label}
                      onClick={() => onChange(`${expression}${c.groupKey}`)}
                      sx={{ fontFamily: 'monospace', fontSize: 11 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Collapse>
      </Box>
    </Stack>
  );
}

export default FormulaRuleEditor;
